/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, keyframes } from '@emotion/react';
import {
  Pane,
  Heading,
  Paragraph,
  minorScale,
  majorScale,
  Text,
} from 'evergreen-ui';
import { useQuery, gql, useReactiveVar } from '@apollo/client';
import { get, trim } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import useCurrencies, {
  defaultCurrencyTemp,
} from '../../../../../hooks/use-currencies';
import { authUserVar } from '../../../../../helpers/auth';
import Table from '../../../../../components/table/table';
import FormSelectBox from '../../../../../components/form-select-box/form-select-box';
import { useMediaQuery } from 'react-responsive';

import useResponsiveness from '../../../../../hooks/use-responsiveness';
import {
  ISOFormatTime,
  numberToCurrency,
  formatDateToOwnFormatWl,
  formatDatetimeWithUTC,
} from '../../../../../helpers/functions';
import LoadingStateSpinner from '../../../../../components/loading-state-spinner/loading-state-spinner';
import { ReactComponent as RefreshIcon } from '../../../../../assets/icons/refresh.svg';
import { ReactComponent as ArrowIcon } from '../../../../../assets/icons/arrow-outward.svg';
import useDidMountEffect from '../../../../../hooks/use-did-mount-effect';
import CustomPagination from '../../../../../components/custom-pagination/custom-pagination';
import ErrorStateMsg from '../../../../../components/error-state-msg/error-state-msg';
import { VIEWPORT_BREAKPOINTS } from '../../../../../helpers/enums';
import MobileUserWallet from './mobile-user-wallet';

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const DEFAULT_CURRENCY_QUERY = gql`
  query HostDefaultCurrencyQuery($where: UserFilterInput) {
    allUsers(where: $where) {
      items {
        country {
          currency {
            CurrencyDescription: currencyDescription
            CurrencyId: currencyId
            CurrencyShortcode: currencyShortcode
            CurrencySymbol: currencySymbol
          }
        }
      }
    }
  }
`;

const USER_WALLETS_QUERY = gql`
  query UserWalletsQuery($userId: UUID, $currencyId: Int) {
    userWallets(
      take: 1
      where: { currencyId: { eq: $currencyId }, userId: { eq: $userId } }
    ) {
      items {
        walletId
        accountValue
        dateCreated
        dateModified
      }
    }
  }
`;

const USER_WALLETS_HISTORY_QUERY = gql`
  query GetuserWalletHistory(
    $limit: Int
    $offset: Int
    $searchTerm: String
    $where: UserWalletHistoryFilterInput
  ) {
    userWalletHistory(
      skip: $offset
      take: $limit
      where: $where
      searchTerm: $searchTerm
      order: { dateCreated: DESC }
    ) {
      items {
        dateCreated
        transactionValue
        openingBalance
        closingBalance
        type
        transactionDetails
        currency {
          currencySymbol
        }
      }
      totalCount
    }
  }
`;

const getStatusStyles = (status) => {
  switch (status) {
    case 'DEBIT':
      return {
        bg: '#FBE8E8',
        color: '#AB1616',
      };
    case 'CREDIT':
    default:
      return {
        bg: '#E6F4EB',
        color: '#03923C',
      };
  }
};

const defaultFilters = {
  email: '',
  enquiryReferenceNumber: '',
  fromDate: '',
  toDate: '',
  subject: '',
  name: '',
  message: '',
  status: null,
};

export const UserWalletBadge = ({ text, color, bgColor }) => (
  <Pane
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    paddingY={4}
    paddingX={12}
    borderRadius="16px"
    backgroundColor={bgColor}
  >
    <Text size={300} fontWeight={700} color={color}>
      {text}
    </Text>
  </Pane>
);

export default function UserWallet() {
  const authUser = useReactiveVar(authUserVar);
  const [spinning, setSpinning] = React.useState(false);
  const { data: currencies, loading: currenciesLoading } = useCurrencies();
  const [selectedCurrency, setSelectedCurrency] =
    React.useState(defaultCurrencyTemp);
  const [dataSize] = React.useState(10);
  const { isLaptop } = useResponsiveness();
  const [filters, setFilters] = React.useState(defaultFilters);
  const [genericSearch, setGenericSearch] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [_limit] = React.useState(10); // limit use for displayed data
  const [offset, setOffset] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const isSmallScreen = useMediaQuery({
    query: `(min-width: ${VIEWPORT_BREAKPOINTS.sm}px)`,
  });

  const { data: hostDefaultCurrencys } = useQuery(DEFAULT_CURRENCY_QUERY, {
    variables: {
      where: {
        userId: { eq: authUser.id },
      },
    },
  });

  const hostDefaultCurrency = get(
    hostDefaultCurrencys,
    'allUsers.items[0]',
    false
  );

  React.useEffect(() => {
    if (hostDefaultCurrency?.country?.currency) {
      const hostCurrency = hostDefaultCurrency?.country?.currency;

      setSelectedCurrency({
        ...hostCurrency,
        label: hostCurrency.CurrencyShortcode,
        value: hostCurrency.CurrencyId,
        symbol: hostCurrency.CurrencySymbol,
      });
    }
  }, [hostDefaultCurrency]);

  const {
    loading: userWalletsloading,
    data: userWalletsData,
    refetch,
  } = useQuery(USER_WALLETS_QUERY, {
    variables: {
      userId: authUser.id,
      currencyId: selectedCurrency.CurrencyId,
    },
    fetchPolicy: 'network-only',
    onError: (error) => setErrorMessage(error),
  });

  const userWalletsBalance = get(
    userWalletsData,
    'userWallets.items[0].accountValue',
    0
  );

  const userWalletItem = userWalletsData?.userWallets?.items?.[0];
  const lastUpdated = userWalletItem
    ? formatDateToOwnFormatWl(
        userWalletItem.dateModified || userWalletItem.dateCreated,
        'd LLL yyyy, T'
      )
    : null;

  const walletId = get(userWalletsData, 'userWallets.items[0].walletId', false);

  const filterValue = React.useMemo(() => {
    return {
      ...(genericSearch ? { searchTerm: trim(genericSearch) } : {}),
    };
  }, [filters, genericSearch]);

  const commonWhere = React.useMemo(() => {
    return {
      isActive: { eq: true },
      currencyId: { eq: selectedCurrency.CurrencyId },
      ...(walletId
        ? {
            walletId: {
              eq: walletId,
            },
          }
        : { userId: { eq: authUser.id } }),
      ...(filters.transactionValue
        ? {
            transactionValue: {
              eq: Number(filters.transactionValue),
            },
          }
        : {}),
      ...(filters.direction
        ? {
            type: {
              eq: filters.direction,
            },
          }
        : {}),
      ...(filters.fromDate || filters.toDate
        ? {
            ...(filters.fromDate
              ? {
                  dateCreated: {
                    gte: new Date(filters.fromDate).toISOString(),
                  },
                }
              : {}),
            ...(filters.toDate
              ? {
                  and: {
                    dateCreated: {
                      lte: ISOFormatTime(new Date(filters.toDate)),
                    },
                  },
                }
              : {}),
          }
        : {}),
    };
  }, [filters, genericSearch, selectedCurrency, walletId]);

  const {
    error,
    data,
    loading,
    refetch: historyRefetch,
  } = useQuery(USER_WALLETS_HISTORY_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      offset: offset,
      limit: _limit,
      ...filterValue,
      where: {
        ...commonWhere,
      },
    },
    onCompleted: (data) => {
      const totalRecords = get(data, 'userWalletHistory.totalCount', 0);
      setTotalPages(() => Math.ceil(totalRecords / dataSize));
    },
  });

  const handleRefresh = async () => {
    setSpinning(true);

    // Minimum spinner delay
    const MIN_SPIN_DURATION = 1000; // milliseconds

    const delay = new Promise((resolve) =>
      setTimeout(resolve, MIN_SPIN_DURATION)
    );

    try {
      // Wait for both the API and minimum time
      await Promise.all([
        Promise.all([refetch(), historyRefetch()]), // GraphQL queries
        delay, // Ensures spinner doesn't vanish too quickly
      ]);
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setSpinning(false);
    }
  };

  const userWalletHistoryData = get(data, 'userWalletHistory.items', []);

  const totalCount = get(data, 'userWalletHistory.totalCount', false);

  // Set total number of pages
  React.useEffect(() => {
    if (data) {
      setTotalPages(() =>
        Math.ceil(data?.userWalletHistory?.totalCount / _limit)
      );
    }
  }, [data, _limit]);

  //set offset value on page change
  useDidMountEffect(() => {
    if (data) {
      setOffset((page - 1) * _limit);
    }
  }, [page]);

  //handle page change
  const _setPage = (page) => {
    setPage(page);
  };

  const handlePreviousPage = () => _setPage(page - 1);

  const handleNextPage = () => _setPage(page + 1);

  const renderView = () => {
    if (loading) {
      return <LoadingStateSpinner />;
    }
    if (error) {
      return <ErrorStateMsg msg={error} />;
    }
    if (userWalletHistoryData) {
      if (userWalletHistoryData?.length === 0) {
        return (
          <Pane
            marginY={120}
            height="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Paragraph
              marginTop={minorScale(6)}
              marginBottom={minorScale(4)}
              color="var(--black-lighter-color)"
            >
              There are no recent transactions to show.
            </Paragraph>
          </Pane>
        );
      }
      if (isSmallScreen) {
        return (
          <>
            <Table
              headings={[
                <>&nbsp;</>,
                'Date',
                'Amount',
                'Opening balance',
                'Closing balance',
                'Direction',
                'Details',
              ]}
              data={userWalletHistoryData?.map((tx) => {
                const currencySymbol = get(
                  tx,
                  'currency.currencySymbol',
                  false
                );

                const { bg, color } = getStatusStyles(tx?.type);

                return {
                  logo: (
                    <Pane
                      background={bg}
                      borderRadius="50%"
                      width={40}
                      height={40}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <ArrowIcon
                        width={16}
                        height={16}
                        style={{
                          color: color,
                          transform:
                            tx?.type === 'CREDIT' ? 'rotate(180deg)' : 'none',
                        }}
                      />
                    </Pane>
                  ),
                  date: formatDatetimeWithUTC(get(tx, 'dateCreated', 'N/A')),
                  Amount: numberToCurrency(
                    get(tx, 'transactionValue', 'N/A'),
                    currencySymbol
                  ),
                  openingBalance: numberToCurrency(
                    get(tx, 'openingBalance', 'N/A'),
                    currencySymbol
                  ),

                  closingBalance: numberToCurrency(
                    get(tx, 'closingBalance', 'N/A'),
                    currencySymbol
                  ),
                  direction: (
                    <UserWalletBadge
                      text={get(tx, 'type', 'N/A')}
                      color={color}
                      bgColor={bg}
                    />
                  ),
                  Details: (
                    <Paragraph
                      fontSize="0.875rem"
                      marginTop={minorScale(1)}
                      color="muted"
                      textTransform="capitalize"
                    >
                      {get(tx, 'transactionDetails', 'N/A')?.toLowerCase()}
                    </Paragraph>
                  ),
                };
              })}
            />
          </>
        );
      }

      return (
        <MobileUserWallet
          data={userWalletHistoryData}
          getStatusStyles={getStatusStyles}
        />
      );
    }

    return <LoadingStateSpinner />;
  };

  return (
    <>
      <Pane
        padding={majorScale(2)}
        marginBottom={majorScale(5)}
        border="1px solid var(--gray-default-color)"
        borderRadius={majorScale(2)}
      >
        <Pane
          display="flex"
          justifyContent="space-between"
          marginBottom={majorScale(2)}
        >
          <Pane>
            <Heading
              is="h2"
              marginBottom={minorScale(1)}
              fontSize="1.5em"
              fontWeight={700}
            >
              Wallet balance
            </Heading>

            <Paragraph fontSize="1em">Current available balance</Paragraph>
          </Pane>

          <Pane cursor="pointer" onClick={handleRefresh} height="fit-content">
            <RefreshIcon
              width="24px"
              height="24px"
              css={
                spinning &&
                css`
                  animation: ${rotate} 1s linear infinite;
                `
              }
              style={{ color: 'var(--primary-default-color)' }}
            />
          </Pane>
        </Pane>

        <Pane
          display="flex"
          columnGap={majorScale(2)}
          alignItems="center"
          marginBottom={majorScale(2)}
          flexWrap="wrap"
        >
          {userWalletsloading ? (
            <Skeleton width="100px" height="32px" />
          ) : (
            <Paragraph fontSize="2em" fontWeight={400}>
              {numberToCurrency(
                userWalletsBalance,
                selectedCurrency?.CurrencySymbol
              )}
            </Paragraph>
          )}
          <FormSelectBox
            height={32}
            maxWidth={150}
            width="100%"
            options={currencies?.CurrencyDetails?.CurrencyDetails?.map(
              (currency) => ({
                ...currency,
                value: currency.CurrencyId,
                label: currency.CurrencyShortcode,
              })
            )}
            placeholder="Currency"
            isLoading={currenciesLoading}
            name="selectedCurrency"
            value={selectedCurrency}
            onChange={(selected) => {
              setSelectedCurrency(selected);
              _setPage(1);
              setOffset(0);
            }}
          />
        </Pane>

        <Pane
          borderTop="1px solid var(--gray-default-color)"
          marginLeft={-majorScale(2)}
          marginRight={-majorScale(2)}
        />

        <Paragraph fontSize="1em" fontWeight={400} paddingTop={majorScale(2)}>
          Last updated:{' '}
          {userWalletsloading ? null : lastUpdated ? lastUpdated : 'N/A'}
        </Paragraph>
      </Pane>

      <Pane
        padding={majorScale(2)}
        border="1px solid var(--gray-default-color)"
        borderRadius={majorScale(2)}
      >
        <Heading
          is="h3"
          marginBottom={majorScale(2)}
          fontSize="1.5em"
          fontWeight={700}
        >
          Recent transaction
        </Heading>
        {renderView()}
      </Pane>
      {totalCount >= 10 && (
        <Pane
          marginTop={majorScale(4)}
          display="flex"
          justifyContent="flex-end"
        >
          <CustomPagination
            page={page}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onPageChange={(page) => _setPage(page)}
            totalPages={totalPages}
          />
        </Pane>
      )}
    </>
  );
}
